import { useTranslation } from "react-i18next";
import defaultContent from "../../content/ssiUplineReport";
import commonContent from "../../content/common";
import { getCanonicalTitle } from "../../common/util/Incentives/IncentiveRegistry";
import LeafTabViews, { TabView } from "../../common/leaf/LeafTabViews/LeafTabViews";
import breakpoints from "../../common/scss/breakpoints.module.scss";
import {
  setActiveTabView,
  setSSIUplineData,
  setSSIUplineErrorStatus,
  setSSIUplineLoading,
  ssiUplineReportTabs,
} from "../../reducers/ssiUplineReport";
import { useAppDispatch, useAppSelector } from "../../store";
import { BonusInfoFooter } from "../../common/components/BonusInfoFooter/BonusInfoFooter";
import { ActionReportTitlePortal } from "../ActionReports/common/ActionReportTitlePortal/ActionReportTitlePortal";
import "./SSIUplineReport.scss";
import SSIUplineProfile from "./SSIUplineProfile/SSIUplineProfile";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { SELECTOR_TYPE, setCustomPeriodsOverride, updatePeriod, updateSelectorType } from "../../reducers/period";
import { ROUTE_PATHS } from "../../common/enums/routing-enums";
import { FetchSSIUplineData } from "./SSIUplineDataLayer";
import { getCurrentPeriod } from "../../common/util/period";
import { InWindowSections } from "./InWindow/InWindowSections";
import PreWindow from "./PreWindow/PreWindow";
import { SsiOverviewResponse } from "../../services/StrongStart/ssiAPI.types";
import LeafTile from "../../common/leaf/LeafTile/LeafTile";
import { ErrorTile } from "../../common/components/ErrorTile/ErrorTile";
import TEALIUM from "../../common/enums/tealium-enums";
import { track } from "../../common/util/tealium";
import { OutOfProgram } from "./OutOfProgram/OutOfProgram";
import { MODULE_SSI_UPLINE } from "../../common/constants/export-constants";
import { useExportListData } from "./Export/useExportListData";
import MobileSearch from "./MobileSearch/MobileSearch";
import { EarnedPayment } from "./EarnedPayment/EarnedPayment";
import ServerSideExportCompletedNotify from "../ServerSideExport/ServerSideExportCommon/ServerSideExportNotify/ServerSideExportCompletedNotify/ServerSideExportCompletedNotify";
import ServerSideExportInProgressNotify from "../ServerSideExport/ServerSideExportCommon/ServerSideExportNotify/ServerSideExportInProgressNotify/ServerSideExportInProgressNotify";
import { SSIUplineExportModal } from "../ServerSideExport/SSIUplineExportModal/SSIUplineExportModal";
import { OpenExportV2ModalEvent } from "../ServerSideExport/ServerSideExportCommon/ServerSideExportUtil/ServerSideExportEvents";
import { isExportDisabledBySessionSsoOrigin } from "../../common/util/exportUtil";
export default function SSIUplineReport() {
  const { t } = useTranslation(["ssiUplineReport", "navigation", "common"]);
  const { activeView, activeTabs } = useAppSelector((state) => state.ssiUplineReportTracker);

  const { removePeriodSelectorFromRoute = [] } = useAppSelector((state) => state.boot.configuration);
  const boot = useAppSelector((state) => state.boot);
  const { los: isExportDisabled } = isExportDisabledBySessionSsoOrigin();
  const showExportButton = boot.configuration.ssiUplineReport.enableExport && !isExportDisabled;
  const pageData = useAppSelector((state) => state.ssiUplineReportTracker.ssiUplineData);
  const dispatch = useAppDispatch();
  const { selectedPeriod } = useAppSelector((state) => state.period);
  const currentPeriod = getCurrentPeriod();
  const [windowWidth, setWindowWidth] = useState<number>(window.innerWidth);
  const [isTabHeaderOverflowing, setIsTabHeaderOverflowing] = useState(false);
  const tabContainerRef = useRef<HTMLElement>(null);
  const [selectedAboNumber, setSelectedAboNumber] = useState<string>("");
  const [selectedAboName, setSelectedAboName] = useState<string>("");
  const [openExportModal, setExportModalState] = useState(false);
  const shouldShowMobileSearch = windowWidth <= 1300 || isTabHeaderOverflowing;

  const shouldUsePeriodOverride = !removePeriodSelectorFromRoute.includes(ROUTE_PATHS.SSI_UPLINE_REPORT);
  const periodOverride = shouldUsePeriodOverride ? selectedPeriod : currentPeriod;
  const { ssiUplineData, isLoading, errorStatus, refetch: refetchSSIUplineData } = FetchSSIUplineData(periodOverride);

  useEffect(() => {
    track(
      TEALIUM.U_TAG_METHOD.VIEW,
      "",
      "pre-window_tab",
      "ssi_upline_report",
      `ssi_upline_report:${periodOverride}`,
      "",
    );
  }, [periodOverride]);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      if (window.innerWidth <= 1300) {
        setIsTabHeaderOverflowing(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useLayoutEffect(() => {
    if (windowWidth <= 1300 || isTabHeaderOverflowing) return;

    const tabHeader = tabContainerRef.current?.querySelector<HTMLElement>(".leaf-tab-views__header");
    if (!tabHeader) return;

    const updateTabHeaderOverflow = () => {
      if (tabHeader.scrollWidth > tabHeader.clientWidth) {
        setIsTabHeaderOverflowing(true);
      }
    };

    const resizeObserver = new ResizeObserver(updateTabHeaderOverflow);
    resizeObserver.observe(tabHeader);
    updateTabHeaderOverflow();

    return () => {
      resizeObserver.disconnect();
    };
  }, [activeTabs, isTabHeaderOverflowing, windowWidth]);

  const showExportModalHandler = useCallback(
    (event: CustomEventInit) => {
      if (!showExportButton) return;
      setExportModalState(Boolean(event.detail?.open));
    },
    [showExportButton],
  );

  useEffect(() => {
    window.addEventListener(OpenExportV2ModalEvent, showExportModalHandler);
    return () => {
      window.removeEventListener(OpenExportV2ModalEvent, showExportModalHandler);
    };
  }, [showExportModalHandler]);

  const notifyDownloadLinkOnClick = () => {
    if (!showExportButton) return;
    setExportModalState(true);
  };

  const closeExportModal = () => {
    setExportModalState(false);
  };

  const { searchPool, recordLength } = useMemo(() => {
    const iw = pageData.InWindow;
    const pw = pageData.PreWindow;
    const oop = pageData.OutOfProgram;
    const combinedIW = [...iw.InPaymentMonth, ...iw.InSSIProgram];
    const combinedPW: SsiOverviewResponse[] = [];
    const combinedOOP = oop.ABOs;

    if (pw.Periods.length > 0) {
      pw.Periods.forEach((period) => {
        period.Abos.forEach((abo) => {
          combinedPW.push(abo);
        });
      });
    }

    const earnedPaymentAbos: SsiOverviewResponse[] = [];
    if (pageData.EarnedPayment?.Groups) {
      pageData.EarnedPayment.Groups.forEach((group) => {
        (group.ABOs ?? []).forEach((abo) => {
          earnedPaymentAbos.push({
            Aff: abo.Aff,
            Abo: abo.Abo,
            IsoCountry: abo.IsoCountry,
            AboName: abo.AboName,
            SponsorName: abo.SponsorAboName,
            SponsorABO: abo.SponsorABO,
          } as SsiOverviewResponse);
        });
      });
    }

    const computedRecordLength = {
      inWindow: combinedIW.length,
      preWindow: combinedPW.length,
      outOfProgram: combinedOOP.length,
      earnedPayment: earnedPaymentAbos.length,
    };

    const searchArray = [...combinedIW, ...combinedPW, ...combinedOOP, ...earnedPaymentAbos];
    const updatedSearchArray = searchArray.map((item) => {
      if (item.AboName === "") {
        return { ...item, AboName: t("common:confidentialUser", defaultContent.confidentialUser).toString() };
      }
      return item;
    });

    return {
      searchPool: updatedSearchArray,
      recordLength: computedRecordLength,
    };
  }, [pageData, t]);

  useEffect(() => {
    dispatch(setSSIUplineData(ssiUplineData));
    dispatch(setSSIUplineLoading(isLoading));
    dispatch(setSSIUplineErrorStatus(errorStatus));
  }, [dispatch, ssiUplineData, isLoading, errorStatus]);

  const { monthPeriods } = useAppSelector((state) => state.period);

  useEffect(() => {
    const customPeriods = [currentPeriod, monthPeriods[1]].filter(Boolean);
    dispatch(setCustomPeriodsOverride({ periods: customPeriods }));
    dispatch(updateSelectorType(SELECTOR_TYPE.CONFIGURABLE));
    if (!customPeriods.includes(selectedPeriod)) {
      dispatch(updatePeriod({ period: currentPeriod }));
    }
  }, [dispatch, currentPeriod, monthPeriods, selectedPeriod]);

  const { data: ssiExportData, refetch: refetchExportListData } = useExportListData(
    { moduleId: MODULE_SSI_UPLINE },
    { skip: !showExportButton },
  );

  useEffect(() => {
    if (showExportButton) refetchExportListData();
  }, [activeView, refetchExportListData, showExportButton]);

  const setViewOnClick = (activeView: ssiUplineReportTabs) => {
    dispatch(setActiveTabView(activeView));

    const tealiumTabName = {
      [ssiUplineReportTabs.GRACE_PERIOD_TAB]: "pre-window",
      [ssiUplineReportTabs.IN_SSI_PROGRAM_TAB]: "in-window",
      [ssiUplineReportTabs.EARNED_PAYMENT_TAB]: "earned_payment",
      [ssiUplineReportTabs.OUT_SSI_PROGRAM_TAB]: "out_of_program",
    };

    track(
      TEALIUM.U_TAG_METHOD.LINK,
      "",
      tealiumTabName[activeView],
      "ssi_upline_report",
      "",
      `tab_selection_ssi_report`,
    );
  };

  const setSelectedAboData = (num: string, name: string) => {
    setSelectedAboNumber(num);
    setSelectedAboName(name);
  };

  const mobileSearch = useMemo(
    () => (
      <MobileSearch
        pageData={pageData}
        searchPool={searchPool}
        windowWidth={windowWidth}
        isLoading={isLoading}
        showExportButton={showExportButton}
        activeView={activeView}
        ssiExportData={ssiExportData}
        recordLength={recordLength}
        setSelectedAboData={setSelectedAboData}
        selectedAboName={selectedAboName}
      />
    ),
    [
      activeView,
      isLoading,
      pageData,
      recordLength,
      searchPool,
      selectedAboName,
      showExportButton,
      ssiExportData,
      windowWidth,
    ],
  );

  if (errorStatus && !isLoading) {
    return (
      <LeafTile hideBorder={true}>
        <ErrorTile
          clickFunction={refetchSSIUplineData}
          errorStatus={errorStatus}
          isLoading={isLoading}
          tealiumTrackId="SSI_Upline_Report"
        />
      </LeafTile>
    );
  }

  const GracePeriodView = () => {
    return (
      <div>
        {!isLoading && recordLength.preWindow > 0 && (
          <p className="tabDesc">{t("tabs.inGracePeriod.desc", defaultContent.tabs.inGracePeriod.desc).toString()}</p>
        )}
        {shouldShowMobileSearch && mobileSearch}
        <PreWindow ssiUplineData={pageData} isLoading={isLoading} selectedAboNumber={selectedAboNumber} />
      </div>
    );
  };

  const InSsiProgramView = () => {
    return (
      <div>
        {(pageData.InWindow.InPaymentMonth.length > 0 || pageData.InWindow.InSSIProgram.length > 0) && (
          <p className="tabDesc">{t("tabs.inSSIProgram.desc", defaultContent.tabs.inSSIProgram.desc).toString()}</p>
        )}
        {shouldShowMobileSearch && mobileSearch}
        <InWindowSections
          ssiUplineData={pageData}
          isLoading={isLoading}
          selectedAboNumber={selectedAboNumber}
          viewType={windowWidth <= Number(breakpoints.mobileLarge) ? "mobile" : "desktop"}
        />
      </div>
    );
  };

  const EarnedPaymentView = () => {
    return (
      <div>
        <p className="tabDesc">{t("tabs.earnedPayment.desc", defaultContent.tabs.earnedPayment.desc).toString()}</p>
        {shouldShowMobileSearch && mobileSearch}
        <EarnedPayment ssiUplineData={pageData} isLoading={isLoading} selectedAboNumber={selectedAboNumber} />
      </div>
    );
  };

  const OutOfSsiProgramView = () => {
    return (
      <div>
        <p className="tabDesc">{t("tabs.outOfSSIProgram.desc", defaultContent.tabs.outOfSSIProgram.desc).toString()}</p>
        {shouldShowMobileSearch && mobileSearch}
        <OutOfProgram ssiUplineData={pageData} isLoading={isLoading} selectedAboNumber={selectedAboNumber} />
      </div>
    );
  };

  const internalTabsView = [
    {
      id: ssiUplineReportTabs.GRACE_PERIOD_TAB,
      title: t("tabs.inGracePeriod.label", defaultContent.tabs.inGracePeriod.label),
      View: GracePeriodView(),
    },
    {
      id: ssiUplineReportTabs.IN_SSI_PROGRAM_TAB,
      title: t("tabs.inSSIProgram.label", defaultContent.tabs.inSSIProgram.label),
      View: InSsiProgramView(),
    },
    {
      id: ssiUplineReportTabs.EARNED_PAYMENT_TAB,
      title: t("tabs.earnedPayment.label", defaultContent.tabs.earnedPayment.label),
      View: EarnedPaymentView(),
    },
    {
      id: ssiUplineReportTabs.OUT_SSI_PROGRAM_TAB,
      title: t("tabs.outOfSSIProgram.label", defaultContent.tabs.outOfSSIProgram.label),
      View: OutOfSsiProgramView(),
    },
  ];

  const tabsView = activeTabs.map((id: string) => ({
    ...internalTabsView.find((item) => item.id === id),
  })) as TabView[];

  const symbol = String(t("disclaimerSymbol", { defaultValue: commonContent.disclaimerSymbol, ns: "common" }));
  const pageTitle = getCanonicalTitle({ id: "ssiUplineReport" }, t, { symbol });

  return (
    <div className="ssiUplineReport">
      <ActionReportTitlePortal title={pageTitle} className="ssiUplineReport__titleHeader" />
      <section className="tabContainer" ref={tabContainerRef}>
        <LeafTabViews
          setViewOnClick={setViewOnClick}
          Views={tabsView}
          activeView={activeView}
          buttonify={true}
          headerRight={shouldShowMobileSearch ? undefined : mobileSearch}
        ></LeafTabViews>
      </section>
      <SSIUplineProfile />
      <div>
        <BonusInfoFooter />
      </div>
      <ServerSideExportInProgressNotify />
      <ServerSideExportCompletedNotify
        moduleId={MODULE_SSI_UPLINE}
        notifyDownloadLinkOnClick={notifyDownloadLinkOnClick}
      />
      <SSIUplineExportModal
        open={openExportModal}
        isFetching={isLoading}
        onCloseCallbackFnInParent={closeExportModal}
        activeView={activeView}
      />
    </div>
  );
}
